/* AYOLIE — light interaction layer (no dependencies). */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Scroll reveal ---- */
  const initReveal = (root = document) => {
    const els = root.querySelectorAll('.ay-reveal:not([data-ay-observed])');
    if (!els.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach((el) => { el.classList.add('is-in'); el.dataset.ayObserved = '1'; });
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => { el.dataset.ayObserved = '1'; io.observe(el); });
  };

  /* ---- Marquee: duplicate the track so the loop is seamless ---- */
  const initMarquee = (root = document) => {
    root.querySelectorAll('.ay-marquee:not([data-ay-ready])').forEach((m) => {
      m.dataset.ayReady = '1';
      const track = m.querySelector('.ay-marquee__track');
      if (!track) return;
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      m.appendChild(clone);
    });
  };

  /* ---- Product gallery: thumbnails scroll the main slider ---- */
  const initGallery = (root = document) => {
    root.querySelectorAll('[data-ay-gallery]:not([data-ay-ready])').forEach((gal) => {
      gal.dataset.ayReady = '1';
      const slides = gal.querySelector('[data-ay-gallery-slides]');
      const thumbs = Array.from(gal.querySelectorAll('[data-ay-gallery-thumb]'));
      if (!slides || thumbs.length < 2) return;

      const setCurrent = (i) => thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));

      thumbs.forEach((t, i) => {
        t.addEventListener('click', () => {
          slides.scrollTo({ left: slides.clientWidth * i, behavior: reduced ? 'auto' : 'smooth' });
          setCurrent(i);
        });
      });

      let raf;
      slides.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(slides.scrollLeft / Math.max(slides.clientWidth, 1));
          setCurrent(Math.min(i, thumbs.length - 1));
        });
      }, { passive: true });
    });
  };

  /* ---- Quantity stepper bound to the real Shopify form input ---- */
  const initQty = (root = document) => {
    root.querySelectorAll('[data-ay-qty]:not([data-ay-ready])').forEach((wrap) => {
      wrap.dataset.ayReady = '1';
      const input = wrap.querySelector('input');
      if (!input) return;
      wrap.querySelectorAll('[data-ay-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = Number(btn.dataset.ayQtyStep) || 1;
          const min = Number(input.min) || 1;
          const next = Math.max(min, (Number(input.value) || min) + step);
          input.value = String(next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  };

  /* ---- Sticky add-to-cart: appears once the main buy box is out of view ---- */
  const initSticky = (root = document) => {
    root.querySelectorAll('[data-ay-sticky]:not([data-ay-ready])').forEach((bar) => {
      bar.dataset.ayReady = '1';
      const watchSel = bar.getAttribute('data-ay-sticky');
      const watch = watchSel ? document.querySelector(watchSel) : null;
      if (!watch || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        ([e]) => bar.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0),
        { threshold: 0 }
      );
      io.observe(watch);

      bar.querySelectorAll('[data-ay-sticky-submit]').forEach((btn) => {
        btn.addEventListener('click', (ev) => {
          const form = document.querySelector(btn.getAttribute('data-ay-sticky-submit'));
          if (!form) return;
          ev.preventDefault();
          const submit = form.querySelector('[type="submit"]');
          if (submit) submit.click(); else form.requestSubmit();
        });
      });
    });
  };

  /* ---- Add to cart: Shopify Standard Actions, with a plain form POST as fallback ---- */
  const initCart = (root = document) => {
    root.querySelectorAll('[data-ay-cart-form]:not([data-ay-ready])').forEach((form) => {
      form.dataset.ayReady = '1';
      form.addEventListener('submit', async (ev) => {
        const actions = window.Shopify && window.Shopify.actions;
        // No Standard Actions on the page: let the browser POST the form to /cart/add.
        if (!actions || !actions.updateCart) return;

        const idField = form.querySelector('[name="id"]');
        const qtyField = form.querySelector('[name="quantity"]');
        const variantId = idField && idField.value;
        if (!variantId) return;

        ev.preventDefault();
        const btn = form.querySelector('[data-ay-cart-submit]');
        const label = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.dataset.ayLabel = label; btn.textContent = btn.dataset.ayLoading || 'Adding…'; }

        try {
          await actions.updateCart(
            { lines: [{ merchandiseId: String(variantId), quantity: Number(qtyField && qtyField.value) || 1 }] },
            { event: { context: 'product' } }
          );
          if (btn) { btn.textContent = btn.dataset.ayAdded || 'Added'; }
          if (actions.openCart) await actions.openCart();
        } catch (err) {
          console.warn('[ayolie] add to cart failed, falling back to /cart/add', err);
          form.submit();
          return;
        } finally {
          window.setTimeout(() => {
            if (btn) { btn.disabled = false; btn.textContent = btn.dataset.ayLabel || label; }
          }, 1400);
        }
      });
    });
  };

  const boot = (root = document) => {
    initReveal(root); initMarquee(root); initGallery(root); initQty(root); initSticky(root); initCart(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  // Re-run inside the theme editor when a section is re-rendered.
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
